import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../Navbar";
import Footer from "../Footer";
import axios from "axios";
import { toast } from "react-toastify";
import CustomSelect from "../SelectSearch/CustomSelect";
import { formatSalesValue } from '../../utils/formatUtils';
import { FiTrash2 } from "react-icons/fi";
import StoreReqItemTable from "./ReceivePurchase/StoreReqItemTable";
import StoreReqPurchaseSummary from "./ReceivePurchase/StoreReqPurchaseSummary";
import { StockableIssuePopup } from "./ReceivePurchase/StockableIssuePop";
import StockableExpiryPopup from "./ReceivePurchase/StockableExpiryPopup";
import ConfirmModal from "../Modal/ConfirmModal";

const STOCK_TYPE_OPTIONS = [
    { value: "Stockable", label: "Stockable" },
    { value: "Non-Stockable", label: "Non-Stockable" },
];

const PAYMENT_MODE_OPTIONS = [
    { value: "Cash", label: "Cash" },
    { value: "Credit", label: "Credit" },
    { value: "Mobile Payment", label: "Mobile Payment" },
    { value: "Credit Card", label: "Credit Card" },
    { value: "Complimentary", label: "Complimentary" },
];

// ─── Main Component ─────────────────────────────────────────────────────────
const ReceivePurchase = () => {
    const { state } = useLocation();
    const navigate = useNavigate();
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const token = localStorage.getItem("token");

    const today = new Date().toLocaleDateString('en-CA');

    const selectedOutlet = state?.selectedOutlet || null;
    const companyNameFromState = state?.companyName || "";
    const reqID = state?.reqID || "";
    const newPurchase = state?.newPurchase ?? false;
    const orderedDate = state?.orderedDate || today;

    // Initialise rows from router state — each row gets its own editable fields
    const initRows = (items = []) =>
        items.map(item => ({
            ...item,
            // Ensure numeric fields are proper numbers
            quantity: parseFloat(item.quantity) || 0,
            Rate: parseFloat(item.Rate) || 0,
            subtotal: parseFloat(item.subtotal) || 0,
            vat: parseFloat(item.vat) || 0,
            total: parseFloat(item.total) || 0,
        }));

    const [rows, setRows] = useState(() => initRows(state?.tableItems || []));

    // Purchase header — pre-fill company from PurchaseEntry if passed
    const [companyName, setCompanyName] = useState(companyNameFromState);
    const [panNumber, setPanNumber] = useState("");
    const [receivedDate, setReceivedDate] = useState(today);
    const [billNumber, setBillNumber] = useState("");
    const [paymentMode, setPaymentMode] = useState("");
    const [discountAmount, setDiscountAmount] = useState("");

    // Issuance state: { [rowIndex]: [{costCenter, qty}] }
    const [issueMap, setIssueMap] = useState({});
    const [popupItem, setPopupItem] = useState(null);

    const [submitLoading, setSubmitLoading] = useState(false);

    const [costCenterOptions, setCostCenterOptions] = useState([]);
    const [loadingCC, setLoadingCC] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const [expiryPopupItems, setExpiryPopupItems] = useState([]);
    const [showExpiryPopup, setShowExpiryPopup] = useState(false);

    useEffect(() => {
        const fetchCostCenters = async () => {
            if (!selectedOutlet || !token) return;

            try {
                setLoadingCC(true);

                const res = await axios.post(`${baseUrl}/get_costcenters_by_outlet`, {
                    outlet_name: selectedOutlet,
                    token
                });

                const formatted = (res.data?.data || []).map((cc) => ({
                    label: cc.costcenter_name,
                    value: cc.costcenter_name,
                    id: cc.id,
                }));

                setCostCenterOptions(formatted);
            } catch (err) {
                console.error("Failed to fetch cost centers", err);
            } finally {
                setLoadingCC(false);
            }
        };

        fetchCostCenters();
    }, [selectedOutlet, baseUrl, token]);

    // Auto-assign non-stockable items on mount
    useEffect(() => {
        const auto = {};
        rows.forEach((item, i) => {
            if (item.stocktype === "Non-Stockable") {
                const dept = (item.Department || "").toLowerCase();
                const costCenter = dept === "beverage" ? "Bar" : "Kitchen";
                auto[i] = [{ costCenter, qty: item.quantity }];
            }
        });
        setIssueMap(auto);
    }, []);

    // ── Row field updater — recalculates subtotal/vat/total on qty or rate change
    const updateRow = (index, field, value) => {
        setRows(prev => prev.map((row, i) => {
            if (i !== index) return row;

            const updated = { ...row, [field]: value };

            // Recalculate when qty or rate changes
            if (field === "quantity" || field === "Rate") {
                const qty = parseFloat(field === "quantity" ? value : row.quantity) || 0;
                const rate = parseFloat(field === "Rate" ? value : row.Rate) || 0;
                const subtotal = qty * rate;
                const vat = updated.taxable ? subtotal * 0.13 : 0;
                return { ...updated, subtotal, vat, total: subtotal + vat };
            }

            // When taxable toggles, recalculate vat too
            if (field === "taxable") {
                const subtotal = row.subtotal;
                const vat = value ? subtotal * 0.13 : 0;
                return { ...updated, vat, total: subtotal + vat };
            }

            return updated;
        }));

        // If qty changed for a row that already has issue assignments, clear them
        // so user re-assigns with the correct new quantity
        if (field === "quantity") {
            setIssueMap(prev => {
                const next = { ...prev };
                delete next[index];
                return next;
            });
        }

        // If stocktype changed to non-stockable, auto-assign; if stockable, clear
        if (field === "stocktype") {
            setIssueMap(prev => {
                const next = { ...prev };
                const updatedRow = rows[index];

                if (value === "Non-Stockable") {
                    const dept = (updatedRow.Department || "").toLowerCase();
                    const costCenter = dept === "beverage" ? "Bar" : "Kitchen";

                    next[index] = [{ costCenter, qty: updatedRow.quantity }];
                } else {
                    // Stockable → REMOVE issue
                    delete next[index];
                }

                return next;
            });
        }
    };

    const handleDeleteRow = (index) => {
        setRows((prev) => prev.filter((_, i) => i !== index));

        setIssueMap((prev) => {
            const updated = { ...prev };
            delete updated[index];

            const reindexed = {};
            Object.keys(updated).forEach((key) => {
                const k = parseInt(key);
                if (k > index) reindexed[k - 1] = updated[k];
                else reindexed[k] = updated[k];
            });

            return reindexed;
        });
    };

    const handleStockableClick = (item, index) => {
        if (item.stocktype !== "Non-Stockable") return;
        setPopupItem({ item, index });
    };

    const handleIssueConfirm = (assignments) => {
        setIssueMap(prev => ({ ...prev, [popupItem.index]: assignments }));
        setPopupItem(null);
    };

    const isComplementary = paymentMode === "Complimentary";

    const subTotal = isComplementary
        ? 0
        : rows.reduce((s, r) => s + (r.subtotal || 0), 0);

    const totalVat = isComplementary
        ? 0
        : rows.reduce((s, r) => s + (r.vat || 0), 0);

    const discount = isComplementary
        ? 0
        : parseFloat(discountAmount) || 0;

    const grandTotal = isComplementary
        ? 0
        : subTotal + totalVat - discount;

    const allIssued = rows.every((item, i) => {
        if (item.stocktype !== "Non-Stockable") return true;
        return issueMap[i] && issueMap[i].length > 0;
    });

    const handleExpiryConfirm = (dates) => {
        const expiryByItemName = {};
        expiryPopupItems.forEach((item, popupIdx) => {
            if (dates[popupIdx]) {
                expiryByItemName[item.ItemName] = dates[popupIdx];
            }
        });

        const updatedRows = rows.map((row) => {
            const enteredExpiry = expiryByItemName[row.ItemName];
            if (enteredExpiry) {
                return { ...row, expiryInput: enteredExpiry };
            }
            return row;
        });

        setRows(updatedRows);
        setShowExpiryPopup(false);
        setExpiryPopupItems([]);

        setTimeout(() => handleSubmit(updatedRows), 0);
    };

    const triggerPrint = (response) => {
        const { purchase } = response;
        const now = new Date();
        const dateStr = now.toLocaleDateString('en-US');
        const timeStr = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

        const dIssue = Object.keys(issueMap).length > 0;
        const totalVatVal = isComplementary ? 0 : rows.reduce((s, r) => s + (r.vat || 0), 0);

        const printWindow = window.open('', '_blank', 'width=900,height=700');
        printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
            <title>Purchase Receipt - GRN #${purchase.grn}</title>
            <style>
                *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

                html, body {
                    width: 100%;
                    height: 100%;
                    font-family: 'Courier New', Courier, monospace;
                    font-size: 13px;
                    background: white;
                    color: #000;
                }

                .page {
                    width: 100%;
                    min-height: 100vh;
                    padding: 24px 32px;
                    display: flex;
                    flex-direction: column;
                }

                .header {
                    text-align: center;
                    font-size: 17px;
                    font-weight: bold;
                    letter-spacing: 1px;
                    text-transform: uppercase;
                    margin-bottom: 4px;
                }

                .sub-header {
                    text-align: center;
                    font-size: 13px;
                    margin-bottom: 16px;
                    color: #333;
                }

                .meta-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 4px 16px;
                    margin-bottom: 16px;
                    font-size: 13px;
                }

                .meta-item { display: flex; gap: 6px; }
                .meta-item .label { font-weight: 700; white-space: nowrap; }
                .meta-item .value { color: #111; }

                .section-title {
                    font-size: 13px;
                    font-weight: bold;
                    margin-bottom: 6px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                hr.divider { border: none; border-top: 1px dashed #000; margin: 10px 0; }
                hr.divider-solid { border: none; border-top: 2px solid #000; margin: 10px 0; }

                table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 12px;
                }

                thead th {
                    text-align: left;
                    padding: 5px 6px;
                    border-top: 1px solid #000;
                    border-bottom: 1px solid #000;
                    white-space: nowrap;
                }

                tbody td {
                    padding: 4px 6px;
                    vertical-align: top;
                    word-break: break-word;
                }

                tbody tr:not(:last-child) td {
                    border-bottom: 1px dotted #ccc;
                }

                th.right, td.right { text-align: right; }

                .summary-block {
                    margin-top: 12px;
                    display: flex;
                    flex-direction: column;
                    align-items: flex-end;
                    gap: 4px;
                    font-size: 13px;
                }

                .summary-row {
                    display: flex;
                    gap: 16px;
                    justify-content: flex-end;
                }

                .summary-row .s-label {
                    font-weight: 600;
                    min-width: 120px;
                    text-align: right;
                }

                .summary-row .s-value {
                    min-width: 80px;
                    text-align: right;
                }

                .grand-total .s-label,
                .grand-total .s-value {
                    font-size: 15px;
                    font-weight: bold;
                    border-top: 1px solid #000;
                    padding-top: 4px;
                    margin-top: 4px;
                }

                .footer {
                    margin-top: auto;
                    padding-top: 24px;
                    font-size: 13px;
                    display: flex;
                    justify-content: space-between;
                }

                .print-btn {
                    display: block;
                    margin: 16px auto 0;
                    padding: 8px 24px;
                    font-size: 14px;
                    cursor: pointer;
                    background: #1a56db;
                    color: white;
                    border: none;
                    border-radius: 4px;
                }

                @media print {
                    html, body { font-size: 12px; }
                    .page { padding: 12px 16px; min-height: 100vh; }
                    .print-btn { display: none !important; }
                    table { font-size: 11px; }
                    @page { size: auto; margin: 10mm; }
                }
            </style>
        </head>
        <body>
            <div class="page">
                <div class="header">Central Store Purchase Record</div>
                <div class="sub-header">${selectedOutlet || ''}</div>

                <div class="meta-grid">
                    <div class="meta-item"><span class="label">GRN #:</span><span class="value">${purchase.grn}</span></div>
                    <div class="meta-item"><span class="label">Date:</span><span class="value">${dateStr}</span></div>
                    <div class="meta-item"><span class="label">Vendor:</span><span class="value">${companyName}</span></div>
                    <div class="meta-item"><span class="label">Time:</span><span class="value">${timeStr}</span></div>
                    <div class="meta-item"><span class="label">Req #:</span><span class="value">${purchase.outlet_purchase_req_id}</span></div>
                    <div class="meta-item"><span class="label">Status:</span><span class="value">Received</span></div>
                    <div class="meta-item"><span class="label">D Issue:</span><span class="value">${dIssue}</span></div>
                    <div class="meta-item"><span class="label">Received:</span><span class="value">${receivedDate}</span></div>
                    <div class="meta-item"><span class="label">Bill No:</span><span class="value">${billNumber}</span></div>
                    <div class="meta-item"><span class="label">Payment:</span><span class="value">${paymentMode}</span></div>
                </div>

                <hr class="divider-solid" />

                <div class="section-title">Items</div>

                <table>
                    <thead>
                        <tr>
                            <th>Item</th>
                            <th>Stock Type</th>
                            <th>Unit</th>
                            <th class="right">Qty</th>
                            <th class="right">Rate</th>
                            <th class="right">Tax (13%)</th>
                            <th class="right">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${rows.map(item => {
            const qty = parseFloat(item.quantity) || 0;
            const rate = parseFloat(item.Rate) || 0;
            const subtotal = qty * rate;
            const tax = item.taxable ? (subtotal * 0.13).toFixed(2) : '0.00';
            const total = item.taxable ? (subtotal * 1.13).toFixed(2) : subtotal.toFixed(2);
            return `
                                <tr>
                                    <td>${item.ItemName || ''}</td>
                                    <td>${item.stocktype || ''}</td>
                                    <td>${item.primary_unit || ''}</td>
                                    <td class="right">${qty.toFixed(2)}</td>
                                    <td class="right">${rate.toFixed(2)}</td>
                                    <td class="right">${tax}</td>
                                    <td class="right">${total}</td>
                                </tr>
                            `;
        }).join('')}
                    </tbody>
                </table>

                <hr class="divider-solid" />

                <div class="summary-block">
                    <div class="summary-row">
                        <span class="s-label">Sub Total:</span>
                        <span class="s-value">${formatSalesValue(subTotal)}</span>
                    </div>
                    <div class="summary-row">
                        <span class="s-label">Tax Amount:</span>
                        <span class="s-value">${formatSalesValue(totalVatVal)}</span>
                    </div>
                    <div class="summary-row">
                        <span class="s-label">Discount:</span>
                        <span class="s-value">${formatSalesValue(discount)}</span>
                    </div>
                    <div class="summary-row grand-total">
                        <span class="s-label">Grand Total:</span>
                        <span class="s-value">${formatSalesValue(grandTotal)}</span>
                    </div>
                </div>

                <hr class="divider" />

                <div class="footer">
                    <span><strong>FOR:</strong> ${selectedOutlet || ''}</span>
                </div>

                <button class="print-btn" onclick="window.print()">🖨 Print</button>
            </div>

            <script>
                setTimeout(() => window.print(), 600);
            </script>
        </body>
        </html>
    `);
        printWindow.document.close();
    };

    const handleSubmit = async (finalRows = rows) => {
        if (!companyName) {
            toast.error("Please enter company name.");
            return null;
        }

        if (!receivedDate) {
            toast.error("Please enter received date.");
            return null;
        }

        if (!billNumber) {
            toast.error("Please enter bill number.");
            return null;
        }

        if (!allIssued) {
            toast.error("Please assign all items to cost centers.");
            return null;
        }

        const stockableWithExpiry = finalRows.filter(
            item =>
                item.stocktype === "Stockable" &&
                item.exp_date &&
                item.exp_date !== "" &&
                !item.expiryInput
        );

        if (stockableWithExpiry.length > 0) {
            setExpiryPopupItems(stockableWithExpiry);
            setShowExpiryPopup(true);
            return;
        }

        setSubmitLoading(true);

        try {
            const now = new Date();
            const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}T${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;

            const costCenterMap = {};

            finalRows.forEach((item, idx) => {
                if (item.stocktype !== "Non-Stockable") return;

                const assignments = issueMap[idx] || [];

                assignments.forEach(({ costCenter, qty }) => {
                    if (!costCenterMap[costCenter]) costCenterMap[costCenter] = [];

                    costCenterMap[costCenter].push({
                        ItemName: item.ItemName || "",
                        GroupName: item.GroupName || "",
                        BrandName: item.BrandName || "",
                        Amount: parseFloat(qty),
                        UOM: item.primary_unit || "",
                        Rate: item.Rate,
                    });
                });
            });

            const stocktransfer = Object.entries(costCenterMap).map(([costCenter, items]) => ({
                CostCenter: costCenter,
                Date: today,
                ItemDetailsList: items,
            }));

            const payload = {
                RequisitionType: "Purchase Order",
                Date: orderedDate,
                TotalAmount: grandTotal,
                TaxAmount: totalVat,
                Company_Name: companyName || "",
                State: "Received",
                ReceivedDate: receivedDate || null,
                purchaseBillNumber: billNumber,
                DiscountAmount: discount > 0 ? discount : null,
                Outlet_Name: selectedOutlet || "",
                purchase_from: "web",
                company_pan: panNumber,
                payment_mode: paymentMode,

                RequisitionDetailsList: finalRows.map(item => ({
                    ItemID: item.ItemID || null,
                    UnitsOrdered: item.quantity,
                    Rate: item.Rate,
                    Name: item.ItemName || "",
                    BrandName: item.BrandName || "",
                    Code: item.Code || "",
                    UOM: item.primary_unit || "",
                    StockType: item.stocktype || "",
                    Department: item.Department || "",
                    GroupName: item.GroupName || "",
                    ExpDate: item.expiryInput || item.exp_date || null,
                    Status: "In Stock",
                    Taxable: !!item.taxable ? "Yes" : "No",
                })),

                stocktransfers: stocktransfer.length > 0 ? stocktransfer : [],

                purchase_id_to_delete: reqID,
            };

            const apiResponse = await axios.post(`${baseUrl}/purchase_with_transfer`, payload);

            setRows(initRows([]));
            setCompanyName("");
            setPanNumber("");
            setReceivedDate(today);
            setBillNumber("");
            setPaymentMode("");
            setDiscountAmount("");

            setIssueMap({});
            setPopupItem(null);

            setExpiryPopupItems([]);
            setShowExpiryPopup(false);

            setShowConfirmModal(false);

            toast.success("Purchase order received successfully!", { autoClose: 3000 });
            setTimeout(() => triggerPrint(apiResponse.data), 1000);
            setTimeout(() => navigate('/store-reqs'), 1500);

        } catch (err) {
            console.error("Submission error:", err);
            toast.error(
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                "Submission failed. Please try again.",
                { autoClose: 5000 }
            );
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleDeleteClick = () => {
        setShowConfirmModal(true);
    };

    const handleDeleteCancel = () => {
        setShowConfirmModal(false);
    };

    const handleDeleteConfirm = async () => {
        if (!reqID) return;

        setDeleteLoading(true);

        try {
            await axios.post(`${baseUrl}/delete_ordered_purchase`, {
                ordered_purchase_id: reqID,
                outlet_name: selectedOutlet,
                purchase_from: "web",
            });

            toast.success("Order deleted successfully!");

            setShowConfirmModal(false);
            setTimeout(() => navigate(-1), [1000])
        } catch (err) {
            toast.error("Failed to delete order.");
        } finally {
            setDeleteLoading(false);
        }
    };

    const inlineInput = "px-2 py-1 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400 w-full w-[10ch]";

    return (
        <div className="min-h-screen flex flex-col bg-gray-50">
            <Navbar />

            {popupItem && (
                <StockableIssuePopup
                    item={{ ...popupItem.item, quantity: rows[popupItem.index]?.quantity ?? popupItem.item.quantity }}
                    onClose={() => setPopupItem(null)}
                    onConfirm={handleIssueConfirm}
                    costCenterOptions={costCenterOptions}
                    loadingCC={loadingCC}
                />
            )}

            {showExpiryPopup && (
                <StockableExpiryPopup
                    items={expiryPopupItems}
                    onClose={() => setShowExpiryPopup(false)}
                    onConfirm={handleExpiryConfirm}
                />
            )}

            <div className="flex-grow p-4">
                <div className="max-w-7xl mx-auto">
                    <h2 className="text-base font-medium text-gray-800 mb-3">
                        Receive Purchase
                        {selectedOutlet && <span className="text-gray-500 font-semibold"> — {selectedOutlet}</span>}
                    </h2>

                    {/* Purchase Details Card */}
                    <div className="bg-white text-sm rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
                        <h3 className="font-semibold text-gray-500 uppercase tracking-wide mb-2 text-base">Purchase Details</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4 text-sm">
                            <div>
                                <label className="block font-medium text-gray-600 mb-1">Company Name *</label>
                                <input
                                    type="text"
                                    placeholder="e.g. ABC Beverages Pvt Ltd"
                                    value={companyName}
                                    onChange={(e) => setCompanyName(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    disabled
                                />
                            </div>
                            <div>
                                <label className="block font-medium text-gray-600 mb-1">PAN Number</label>
                                <input
                                    type="text"
                                    placeholder="e.g. 123456789"
                                    value={panNumber}
                                    onChange={(e) => {
                                        const onlyNums = e.target.value.replace(/\D/g, "");
                                        setPanNumber(onlyNums);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <div>
                                <label className="block font-medium text-gray-600 mb-1">Received Date *</label>
                                <input
                                    type="date"
                                    value={receivedDate}
                                    onChange={(e) => setReceivedDate(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <div>
                                <label className="block font-medium text-gray-600 mb-1">Bill Number *</label>
                                <input
                                    type="text"
                                    placeholder="e.g. 12345"
                                    value={billNumber}
                                    onChange={(e) => {
                                        const onlyNums = e.target.value.replace(/\D/g, "");
                                        setBillNumber(onlyNums);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <div>
                                <label className="block font-medium text-gray-600 mb-1">Payment Mode *</label>
                                <CustomSelect
                                    options={PAYMENT_MODE_OPTIONS}
                                    value={paymentMode}
                                    placeholder="Select payment mode"
                                    onChange={(val) => setPaymentMode(val)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Items Table */}
                    <StoreReqItemTable
                        rows={rows}
                        issueMap={issueMap}
                        updateRow={updateRow}
                        inlineInput={inlineInput}
                        STOCK_TYPE_OPTIONS={STOCK_TYPE_OPTIONS}
                        formatSalesValue={formatSalesValue}
                        handleStockableClick={handleStockableClick}
                        navigate={navigate}
                        newPurchase={newPurchase}
                        handleDeleteConfirm={handleDeleteClick}
                        handleDeleteRow={handleDeleteRow}
                        deleteLoading={deleteLoading}
                        CustomSelect={CustomSelect}
                        FiTrash2={FiTrash2}
                    />

                    {/* Summary + Actions */}
                    <StoreReqPurchaseSummary
                        selectedOutlet={selectedOutlet}
                        companyName={companyName}
                        panNumber={panNumber}
                        receivedDate={receivedDate}
                        billNumber={billNumber}
                        formatSalesValue={formatSalesValue}
                        subTotal={subTotal}
                        totalVat={totalVat}
                        discountAmount={discountAmount}
                        setDiscountAmount={setDiscountAmount}
                        isComplementary={isComplementary}
                        discount={discount}
                        grandTotal={grandTotal}
                        navigate={navigate}
                        deleteLoading={deleteLoading}
                        handleSubmit={handleSubmit}
                        submitLoading={submitLoading}
                        rows={rows}
                    />

                </div>

                <ConfirmModal
                    isOpen={showConfirmModal}
                    title="Confirm Delete"
                    message={`Are you sure you want to delete this purchase order? This action cannot be undone.`}
                    confirmText="Delete"
                    cancelText="Cancel"
                    onConfirm={handleDeleteConfirm}
                    onCancel={handleDeleteCancel}
                    onClose={handleDeleteCancel}
                    loading={deleteLoading}
                    danger
                />
            </div>

            <Footer />
        </div>
    );
};

export default ReceivePurchase;